import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  CheckCircle2,
  ImagePlus,
  Info,
  MapPin,
  Save,
  Sparkles,
  WalletCards,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { adminApi, publicAssetUrl } from "../../lib/adminApi";

const defaultForm = {
  title: "",
  project_type: "TETFund",
  funding_source: "TETFund Intervention",
  budget_amount: "",
  contractor: "",
  location: "Main Campus",
  start_date: "",
  expected_completion_date: "",
  completion_percentage: 0,
  description: "",
  impact: "",
  status: "ongoing",
  is_featured: false,
};

const projectTypes = [
  "TETFund",
  "Infrastructure",
  "ICT",
  "Research",
  "Library Development",
  "Laboratory Upgrade",
  "Workshop Upgrade",
  "Institutional Project",
];

const fundingSources = [
  "TETFund Intervention",
  "TETFund Annual Intervention",
  "TETFund Zonal Intervention",
  "TETFund Special Intervention",
  "Institutional Funding",
  "Government Funding",
  "Partner Funding",
];

function normalizeDate(value) {
  return value ? String(value).slice(0, 10) : "";
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, Number(value || 0)));
}

export default function AdminProjectForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [file, setFile] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    adminApi(`/api/admin/projects/${id}`)
      .then((data) => {
        if (!mounted) return;
        setForm({
          title: data.title || "",
          project_type: data.project_type || "TETFund",
          funding_source: data.funding_source || "TETFund Intervention",
          budget_amount: data.budget_amount || "",
          contractor: data.contractor || "",
          location: data.location || "",
          start_date: normalizeDate(data.start_date),
          expected_completion_date: normalizeDate(data.expected_completion_date),
          completion_percentage: clampPercent(data.completion_percentage),
          description: data.description || "",
          impact: data.impact || "",
          status: data.status || "ongoing",
          is_featured: Boolean(data.is_featured),
        });
        setExistingImage(publicAssetUrl(data.image_url));
      })
      .catch((err) => setError(err.message || "Could not load project"));
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const completion = useMemo(() => clampPercent(form.completion_percentage), [form.completion_percentage]);

  function update(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        fd.append(key, key === "is_featured" ? (value ? "1" : "0") : value);
      });
      if (file) fd.append("image", file);

      await adminApi(id ? `/api/admin/projects/${id}` : "/api/admin/projects", {
        method: id ? "PUT" : "POST",
        body: fd,
      });

      navigate("/adxm/projects");
    } catch (err) {
      setError(err.message || "Project could not be saved");
    } finally {
      setSaving(false);
    }
  }

  const imageSrc = preview || existingImage;

  return (
    <AdminLayout
      title={id ? "Edit TETFund Project" : "Add TETFund Project"}
      subtitle="Create public project tracker records with status, budget, contractor, location, progress and impact information."
      actions={
        <Link to="/adxm/projects" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">
          <ArrowLeft size={18} /> Back to Projects
        </Link>
      }
    >
      <form onSubmit={submit} className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[1fr_380px]">
        <section className="space-y-6">
          {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 font-bold text-red-700">{error}</div>}

          <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <span className="rounded-2xl bg-emerald-700 p-3 text-white"><Sparkles size={24} /></span>
              <div>
                <h2 className="text-xl font-black text-slate-950">Project information</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Admin can add TETFund projects here. After saving, the record appears on the public TETFund project tracker page.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-5 flex items-center gap-2 text-lg font-black text-slate-950">
              <Building2 size={21} className="text-emerald-700" /> Core details
            </h3>

            <div className="grid gap-5 md:grid-cols-2">
              <label className="md:col-span-2">
                <span className="font-bold text-slate-700">Project title</span>
                <input
                  required
                  name="title"
                  value={form.title}
                  onChange={update}
                  placeholder="e.g. TETFund ICT Laboratory Upgrade"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                />
              </label>

              <label>
                <span className="font-bold text-slate-700">Project type</span>
                <select name="project_type" value={form.project_type} onChange={update} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100">
                  {projectTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </label>

              <label>
                <span className="font-bold text-slate-700">Funding source</span>
                <select name="funding_source" value={form.funding_source} onChange={update} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100">
                  {fundingSources.map((source) => <option key={source} value={source}>{source}</option>)}
                </select>
              </label>

              <label>
                <span className="font-bold text-slate-700">Budget amount / project cost</span>
                <input
                  type="number"
                  name="budget_amount"
                  value={form.budget_amount}
                  onChange={update}
                  placeholder="450000000"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                />
              </label>

              <label>
                <span className="font-bold text-slate-700">Contractor / implementing unit</span>
                <input
                  name="contractor"
                  value={form.contractor}
                  onChange={update}
                  placeholder="Contractor name or internal unit"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                />
              </label>

              <label>
                <span className="font-bold text-slate-700">Location</span>
                <input
                  name="location"
                  value={form.location}
                  onChange={update}
                  placeholder="Main Campus, Bida"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                />
              </label>

              <label>
                <span className="font-bold text-slate-700">Status</span>
                <select name="status" value={form.status} onChange={update} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100">
                  <option value="planned">Planned</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="suspended">Suspended</option>
                </select>
              </label>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-5 flex items-center gap-2 text-lg font-black text-slate-950">
              <CalendarDays size={21} className="text-blue-700" /> Timeline and progress
            </h3>

            <div className="grid gap-5 md:grid-cols-3">
              <label>
                <span className="font-bold text-slate-700">Start date</span>
                <input type="date" name="start_date" value={form.start_date} onChange={update} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
              </label>

              <label>
                <span className="font-bold text-slate-700">Expected completion</span>
                <input type="date" name="expected_completion_date" value={form.expected_completion_date} onChange={update} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
              </label>

              <label>
                <span className="font-bold text-slate-700">Completion percentage</span>
                <input type="number" min="0" max="100" name="completion_percentage" value={form.completion_percentage} onChange={update} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
              </label>
            </div>

            <div className="mt-5">
              <div className="mb-2 flex justify-between text-sm font-black text-slate-600">
                <span>Preview progress</span>
                <span>{completion}%</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 transition-all" style={{ width: `${completion}%` }} />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-5 flex items-center gap-2 text-lg font-black text-slate-950">
              <Info size={21} className="text-violet-700" /> Public description
            </h3>

            <label className="block">
              <span className="font-bold text-slate-700">Project description</span>
              <textarea name="description" rows="7" value={form.description} onChange={update} placeholder="Describe the project, scope, facilities, current work and status..." className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100" />
            </label>

            <label className="mt-5 block">
              <span className="font-bold text-slate-700">Expected impact</span>
              <textarea name="impact" rows="5" value={form.impact} onChange={update} placeholder="Explain how the project benefits students, staff, laboratories, classrooms or institutional delivery..." className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100" />
            </label>
          </div>
        </section>

        <aside className="space-y-6">
          <div className="sticky top-24 space-y-6">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex h-64 items-center justify-center bg-slate-100">
                {imageSrc ? (
                  <img src={imageSrc} alt="Project preview" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <div className="text-center">
                    <ImagePlus className="mx-auto h-12 w-12 text-slate-400" />
                    <p className="mt-2 text-sm font-bold text-slate-500">No project image selected</p>
                  </div>
                )}
              </div>
              <div className="p-5">
                <label className="block">
                  <span className="font-bold text-slate-700">Project image</span>
                  <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold" />
                </label>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-black text-slate-950">Publish settings</h3>
              <label className="mt-4 flex items-center gap-3 rounded-2xl bg-yellow-50 p-4 font-bold text-slate-700">
                <input type="checkbox" name="is_featured" checked={form.is_featured} onChange={update} />
                Feature this project
              </label>

              <div className="mt-4 grid gap-3 text-sm">
                <div className="flex items-center gap-2 rounded-2xl bg-slate-50 p-3">
                  <MapPin size={17} className="text-emerald-700" />
                  <span className="font-bold text-slate-600">{form.location || "Location not set"}</span>
                </div>
                <div className="flex items-center gap-2 rounded-2xl bg-slate-50 p-3">
                  <WalletCards size={17} className="text-blue-700" />
                  <span className="font-bold text-slate-600">{form.budget_amount ? `₦${Number(form.budget_amount).toLocaleString()}` : "Budget not set"}</span>
                </div>
                <div className="flex items-center gap-2 rounded-2xl bg-slate-50 p-3">
                  <CheckCircle2 size={17} className="text-violet-700" />
                  <span className="font-bold capitalize text-slate-600">{form.status}</span>
                </div>
              </div>

              <button disabled={saving} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-700 px-5 py-4 font-black text-white shadow hover:bg-emerald-800 disabled:opacity-60">
                <Save size={18} /> {saving ? "Saving Project..." : "Save TETFund Project"}
              </button>
            </div>
          </div>
        </aside>
      </form>
    </AdminLayout>
  );
}
